import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import LoadingSpinner from "../../common/loaders/loadingSpinner";

import { BreadCrumbs } from "../../common/layout";
import useApi from "../../../hooks/useApi";

import prospectService from "../../../services/prospectService";
import entityService from "../../../services/entityService";
import authService from "../../../services/authService";

import SettingsContact from "../../entities/_settingsContact";
import EditTeamMember from "../widgets/editProspectContact";

import TeamMembersHeader from "./TeamMembersHeader";
import TeamMembersTable from "./TeamMembersTable";
import BulkNewsletterModal from "./BulkNewsletterModal";
import BulkManagingContactModal from "./BulkManagingContactModal";
import AdditionalFiltersModal from "./AdditionalFiltersModal";
import TopWidgets from "./TopWidgets";
import BulkExtendExpiryModal from "./BulkExtendExpiryModal";
import { confirmAlert } from "react-confirm-alert";
import moment from "moment";

import "../../../styles/react-table.css";
import "react-table/react-table.css";

function AllTeamMembers() {
  const tableRef = useRef(null);
  
  const { entity_id, category, constants, country_id } =
    authService.getCurrentUser();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedContact, setSelectedContact] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const [updateModal, setUpdateModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [newsletterModal, setNewsletterModal] = useState(false);
  const [managingContactModal, setManagingContactModal] = useState(false);

  const [newsletter, setNewsletter] = useState("yes");
  const [managingContact, setManagingContact] = useState(null);
  const [managingContactList, setManagingContactList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [owner, setOwner] = useState(null);
  const [country, setCountry] = useState(null);
  const [additionalFiltersModal, setAdditionalFiltersModal] = useState(false);
  const [additionalFilter, setAdditionalFilter] = useState({
    managedBy: null,
    newsletter: null,
    priority: null,
    thisMonth: null,
  });
  const [teamMembers, setTeamMembers] = useState([]);

  const [widgetData, setWidgetData] = useState({
    totalProspects: 0,
    newProspectsThisMonth: 0,
    newProspectsPercent: 0,
    expiredProspects: 0,
    expiredProspectsPercent: 0,
    marketableProspects: 0,
    marketableProspectsPercent: 0,
  });

  const [extendExpiryModal, setExtendExpiryModal] = useState(false);
  const [comment, setComment] = useState("");
  const [prospectStatus, setProspectStatus] = useState("active");
  const [extendedDate, setExtendedDate] = useState(null);

  

  const fetchAllTeamMembersApi = useApi(prospectService.fetchAllTeamMembers);
  const bulkUpdateNewsletterApi = useApi(prospectService.bulkUpdateNewsletter);
  const topWidgetsApi = useApi(prospectService.prospectsConsolidation);
  const bulkUpdateManagingContactApi = useApi(
    prospectService.bulkUpdateManagingContact,
  );
  const loadParentContactsApi = useApi(entityService.getEntityContactsList);
  const deleteProspectContactApi = useApi(prospectService.deleteProspectContact);
  const bulkUpdateExpiryApi = useApi(prospectService.bulkUpdateExpiry);

  const reloadTable = () => {
    if (tableRef.current) {
      tableRef.current.reloadData();
    }
  };

  const getDefaultExtendedDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  };


  const handleDelete = (contactId) => {
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="custom-delete-ui">
                    <h4>Confirm to delete</h4>
                    <p>Are you sure to delete this contact?</p>
                    <button
                        className="btn btn-primary mr-2"
                        onClick={async () => {
                        onClose();

                        const response = await deleteProspectContactApi.request({
                            contact_id: contactId,
                        });

                        if (response?.success) {
                            toast.success(response.message || "Contact deleted successfully");
                            reloadTable();
                            loadWidgets();
                        } else {
                            toast.error(response?.message || "Failed to delete contact");
                        }
                        }}
                    >
                        Yes
                    </button>
                    <button className="btn btn-white" onClick={onClose}>
                        No
                    </button>
                </div>
            ),
        });
    };

  const clearSelectionAndReload = () => {
    setSelectedRows([]);
    reloadTable();
    if (tableRef.current?.clearSelection) {
      tableRef.current.clearSelection();
    }
  };

  const handleCustomSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword.replace(/^ /, ""));
  };

  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      e.target.value.length > 0 &&
      e.target.value.length < 3
    ) {
      toast.error("Enter atleast 3 characters");
    }
  };

  const clearSearch = () => {
    if (searchKeyword.length > 0) {
      setSearchKeyword("");
      return;
    }
    reloadTable();
  };

  const handleEdit = (item) => {
    setSelectedContact(item);
    setUpdateModal(true);
  };

  const handleSettings = (item) => {
    setSelectedContact(item);
    setSettingsModal(true);
  };

  const handleExportToExcel = async () => {
    try {
      const response = await fetchAllTeamMembersApi.request({
        searchKeyword,
        download: true,
        country:  category === constants.CATEGORY.COUNTRY_OFFICE ? country_id : country,
        bp: owner === "all" ? null : owner,
        ...additionalFilter,
      });

      if (response?.data) {
        window.open(response.data, "_blank", "noreferrer");
      } else {
        toast.error("Failed to export team members");
      }
    } catch (error) {
      toast.error("Failed to export team members");
    }
  };

  const checkedRows = selectedRows.filter((obj) => obj.checked);

  const handleNewsletterUpdate = async () => {
    if (!checkedRows.length) {
      toast.error("Please select at least one contact");
      return;
    }

    try {
      setIsLoading(true);

      const response = await bulkUpdateNewsletterApi.request({
        newsletter,
        selectedAttributes: checkedRows,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setNewsletterModal(false);
        setNewsletter("yes");
        clearSelectionAndReload();
        reloadTable();
         loadWidgets();
      } else {
        toast.error(response?.data?.message || "Failed to update newsletter");
      }
    } catch (error) {
      toast.error("Failed to update newsletter");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManagingContactUpdate = async () => {
    if (!checkedRows.length) {
      toast.error("Please select at least one contact");
      return;
    }

    if (!managingContact) {
      toast.error("Please select a managing contact");
      return;
    }

    try {
      setIsLoading(true);

      const response = await bulkUpdateManagingContactApi.request({
        managing_contact: managingContact,
        selectedAttributes: checkedRows,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setManagingContactModal(false);
        setManagingContact(null);
        clearSelectionAndReload();
        reloadTable();
        loadWidgets();
      } else {
        toast.error(
          response?.data?.message || "Failed to update managing contact",
        );
      }
    } catch (error) {
      toast.error("Failed to update managing contact");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOwner = async (value) => {
    setOwner(value === null ? "all" : value);
  };

  const handleCountry = async (value) => {
    setCountry(value);
    setOwner(null);
  };

  const handleUpdateFilter = (property, value) => {
    setAdditionalFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleApplyAdditionalFilter = () => {
    setAdditionalFiltersModal(false);
  };

  const handleResetAdditionalFilter = () => {
    setAdditionalFilter({
      managedBy: null,
      newsletter: null,
      priority: null,
      thisMonth: null,
    });
    setAdditionalFiltersModal(false);
  };

  const isFirstSearchRender = useRef(true);
  const isFirstFilterRender = useRef(true);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }

    const reloadSearchData = async () => {
      if (searchKeyword.length > 2 || searchKeyword.length === 0) {
        try {
          setIsLoading(true);
          reloadTable();
          await loadWidgets();
        } finally {
          setIsLoading(false);
        }
      }
    };

    reloadSearchData();
  }, [searchKeyword]);


  const loadWidgets = async () => {
    try {
      const response = await topWidgetsApi.request({
        searchKeyword,
        country: category === constants.CATEGORY.COUNTRY_OFFICE ? country_id : country,
        bp: owner === "all" ? null : owner,
        ...additionalFilter,
      });

      if (response?.data?.success) {
        setWidgetData(response.data.rows || {});
      }
    } catch (error) {
      toast.error("Failed to load widgets");
    }
  };
   const fetchParentTeamMembers = async () => {
      try {
        const response = await loadParentContactsApi.request(entity_id);
        const rows = response?.data?.rows || [];

        const parentEntityContacts = rows.map((item) => ({
          label: `${item.first_name} ${item.last_name}`,
          value: item.contact_id,
        }));

        setTeamMembers(parentEntityContacts);
        setManagingContactList(parentEntityContacts);
      } catch (error) {
        setTeamMembers([]);
        setManagingContactList([]);
      }
    };

    const handleSubmitExpiryUpdate = async () => {
  if (!prospectStatus) {
    toast.error("Please select status.");
    return;
  }

  if (!extendedDate) {
    toast.error("Please select extend upto date.");
    return;
  }

  if (comment.trim().length < 8) {
    toast.error("Please enter valid comments on the box.");
    return;
  }

  try {
    setIsLoading(true);

    const { success, message } = await bulkUpdateExpiryApi.request({
      data: {
        status: prospectStatus,
        expiry_date: moment(extendedDate).format("YYYY-MM-DD"),
      },
      comment: comment.trim(),
      selectedAttributes: checkedRows,
      statusType: "prospect_expiry_verification",
    });

    if (success === false) {
      toast.error(message);
    } else {
      toast.success(message);
      setExtendExpiryModal(false);
      clearSelectionAndReload();
      loadWidgets();
    }
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    loadWidgets();
    fetchParentTeamMembers();
  }, [entity_id]);

  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }

    if (tableRef.current?.clearSelection) {
      tableRef.current.clearSelection();
    }

    setSelectedRows([]);
    reloadTable();
  }, [owner, country, additionalFilter]);

  useEffect(() => {
    if (extendExpiryModal) {
      setComment("");
      setProspectStatus("active");
      setExtendedDate(getDefaultExtendedDate());
    }
  }, [extendExpiryModal]);


  return (
    <>
      {isLoading && <LoadingSpinner />}

      <Helmet>
        <title>All Prospects | {global.config.title}</title>
      </Helmet>

      <BreadCrumbs
        Header="All Prospects"
        activeModule="All Prospects"
        Route1="/dashboard"
        Label1="Dashboard"
        Route2="/prospects"
        Label2="Prospects"
      />
      
      <TopWidgets
        totalProspects={widgetData.totalProspects}
        newProspectsThisMonth={widgetData.newProspectsThisMonth}
        newProspectsPercent={`${widgetData.newProspectsPercent}%`}
        expiredProspects={widgetData.expiredProspects}
        expiredProspectsPercent={`${widgetData.expiredProspectsPercent}%`}
        marketableProspects={widgetData.marketableProspects}
        marketableProspectsPercent={`${widgetData.marketableProspectsPercent}%`}
        
        onClickTotal={() => {
          setAdditionalFilter({
            managedBy: null,
            newsletter: null,
            priority: null,
            thisMonth: null,
          });
        }}

         onClickNewThisMonth={() => {
          setAdditionalFilter({
            managedBy: null,
            newsletter: null,
            priority: null,
            thisMonth: "1",
          });
        }}

        onClickExpired={() => {
          setAdditionalFilter((prev) => ({
            ...prev,
            managedBy: null,
            newsletter: null,
            priority: "Expired",
            thisMonth: null,
          }));
        }}

        onClickMarketable={() => {
          setAdditionalFilter((prev) => ({
            ...prev,
            managedBy: null,
            priority: null,
            newsletter: "1",
            thisMonth: null,
          }));
        }}

        
      />

      <div className="card overflow-visible">
        <TeamMembersHeader
          searchKeyword={searchKeyword}
          handleCustomSearch={handleCustomSearch}
          handleKeyPress={handleKeyPress}
          clearSearch={clearSearch}
          handleExportToExcel={handleExportToExcel}
          category={category}
          constants={constants}
          country={country}
          owner={owner}
          handleCountry={handleCountry}
          handleOwner={handleOwner}
          onOpenAdditionalFilters={() => setAdditionalFiltersModal(true)}
        />

        <div className="card-body">
          <TeamMembersTable
            tableRef={tableRef}
            service={prospectService.fetchAllTeamMembers}
            searchKeyword={searchKeyword}
            owner={owner}
            country={country}
            additionalFilter={additionalFilter}
            category={category}
            countryId={country_id}
            onSelectRow={setSelectedRows}
            selectedRows={selectedRows}
            entityId={entity_id}
            constants={constants}
            onEdit={handleEdit}
            onSettings={handleSettings}
            onOpenNewsletterModal={() => setNewsletterModal(true)}
            onOpenManagingContactModal={() => setManagingContactModal(true)}
            onOpenExtendExpiryModal={() => setExtendExpiryModal(true)}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <Modal show={updateModal} size="lg" onHide={() => setUpdateModal(false)}>
        <Modal.Header closeButton>
          <h6>Update Team Member</h6>
        </Modal.Header>
        <EditTeamMember
          data={selectedContact}
          onCloseModal={() => setUpdateModal(false)}
          onHandleReload={() => {
            setUpdateModal(false);
            reloadTable();
          }}
        />
      </Modal>

      <Modal
        show={settingsModal}
        onHide={() => setSettingsModal(false)}
        size="lg"
      >
        <SettingsContact
          data={selectedContact}
          onClose={() => setSettingsModal(false)}
          onHandleReload={() => {
            reloadTable();
            loadWidgets();
            setSettingsModal(false);
            reloadTable();
          }}
          type="prospect"
        />
      </Modal>

      <BulkNewsletterModal
        show={newsletterModal}
        onHide={() => {
          setNewsletterModal(false);
          setNewsletter("yes");
        }}
        newsletter={newsletter}
        setNewsletter={setNewsletter}
        onSubmit={handleNewsletterUpdate}
        isLoading={isLoading}
        isVisible={checkedRows.length > 0}
      />

      <BulkManagingContactModal
        show={managingContactModal}
        onHide={() => {
          setManagingContactModal(false);
          setManagingContact(null);
        }}
        managingContact={managingContact}
        setManagingContact={setManagingContact}
        managingContactList={managingContactList}
        onSubmit={handleManagingContactUpdate}
        isLoading={isLoading}
        isVisible={checkedRows.length > 0}
      />

      <AdditionalFiltersModal
        show={additionalFiltersModal}
        onHide={() => setAdditionalFiltersModal(false)}
        onApply={handleApplyAdditionalFilter}
        onReset={handleResetAdditionalFilter}
        additionalFilter={additionalFilter}
        handleUpdateFilter={handleUpdateFilter}
        teamMembers={teamMembers}
        owner={owner}
        category={category}
        constants={constants}
      />

      <BulkExtendExpiryModal
        show={extendExpiryModal}
        onClose={() => setExtendExpiryModal(false)}
        comment={comment}
        setComment={setComment}
        prospectStatus={prospectStatus}
        setProspectStatus={setProspectStatus}
        extendedDate={extendedDate}
        setExtendedDate={setExtendedDate}
        onSubmit={handleSubmitExpiryUpdate}
      />
    </>
  );
}

export default AllTeamMembers;
